import { useEffect, useMemo, useState } from 'react';

import {
  LOOKUP_MIN_CHARS,
  LookupAutocompleteView,
  mapDropdownOptions,
} from '@/shared/ui/molecules/LookupAutocomplete';

import { useCountryDropdown } from '../../../hooks/useCountryDropdown';

export interface CountryAutocompleteProps {
  /** ID do país selecionado; `0` quando nada. */
  readonly value: number;
  /** Rótulo da seleção corrente (exibição inicial em edição, ex.: `countryName`). */
  readonly selectedLabel?: string;
  /** Notifica o novo ID e rótulo (`0`/`''` ao limpar). */
  readonly onChange: (id: number, label: string) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly hasError?: boolean;
}

/**
 * Autocomplete de País para o formulário de Estado. Carrega o dropdown completo
 * de países (`useCountryDropdown`) e filtra **client-side** por nome a partir de
 * {@link LOOKUP_MIN_CHARS} caracteres, sobre a base compartilhada
 * `LookupAutocompleteView` (ADS `Autocomplete`). Guarda o `countryId`.
 */
export function CountryAutocomplete({
  value,
  selectedLabel = '',
  onChange,
  label,
  placeholder,
  hasError,
}: CountryAutocompleteProps) {
  const { options: dropdown, isLoading } = useCountryDropdown();
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState(selectedLabel);

  // Sincroniza o rótulo quando o pai o fornece após a montagem (ex.: edição, cujo
  // valor é populado depois que o autocomplete já montou).
  useEffect(() => {
    setInputText(selectedLabel);
  }, [selectedLabel]);

  const options = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query.length < LOOKUP_MIN_CHARS) return [];
    return mapDropdownOptions(dropdown).filter(option =>
      option.label.toLowerCase().includes(query),
    );
  }, [dropdown, search]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value ? String(value) : ''}
      selectedLabel={inputText}
      options={options}
      isQuerySuccess={!isLoading}
      search={search}
      hasError={hasError ?? false}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id ? Number(id) : 0, itemLabel);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange(0, '');
        setInputText('');
        setSearch('');
      }}
    />
  );
}
